import { useElemSize } from '@toggle/helpers/src/hooks/use-elem-size/useElemSize';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { useRef } from 'react';

import {
  AssetDetailsWithStatus,
  Expectation,
  Scenario,
} from '@/api/asset/asset-schema';
import { createLogGA } from '@/utils/analytics/logGA';

import { ChartEmptyStates } from './components/chart-empty-states/ChartEmptyStates';
import { ChartError } from './components/chart-error/ChartError';
import { ChartFooter } from './components/chart-footer/ChartFooter';
import { ChartLegend } from './components/chart-legend/ChartLegend';
import { ChartTitle } from './components/chart-title/ChartTitle';
import * as S from './EarningsChart.styles';

const HighLowChart = dynamic(
  () => import('@toggle/design-system').then(mod => mod.HighLowChart),
  {
    ssr: false,
  }
);

const paddingDesktop = {
  left: 85,
  right: 150,
};

const paddingMobile = {
  left: 16,
  right: 80,
};

const chartGA = createLogGA('earning_chart');

export interface EarningsChartProps {
  assetDetails: AssetDetailsWithStatus;
  assetName: string;
  expectation: Expectation;
  hasError?: boolean;
  historyMetaCount?: number;
  scenario?: Scenario;
  chartAlt?: string;
  isEmptyState?: boolean;
}

export const EarningsChart = ({
  assetDetails,
  assetName,
  expectation,
  hasError,
  historyMetaCount,
  scenario,
  isEmptyState,
}: EarningsChartProps) => {
  const { t } = useTranslation('earnings');
  const chartWrapperRef = useRef(null);
  const { width } = useElemSize(chartWrapperRef);

  const mappedData = scenario?.data.map(item => ({
    ...item,
    label: t(item.period),
  }));

  const onRetry = () => {
    createLogGA('Retry')('click_try_again', {
      assetName,
      location: 'errorScreen',
    });
  };

  if (hasError) {
    return (
      <S.EarningsChartRoot data-testid="earnings-chart-error">
        <S.Title />
        <ChartError onRetry={onRetry} />
      </S.EarningsChartRoot>
    );
  }

  if (isEmptyState || !mappedData) {
    return (
      <S.EarningsChartRoot data-testid="earnings-chart-empty">
        <S.Title />
        <ChartEmptyStates
          assetDetails={assetDetails}
          expectation={expectation}
        />
      </S.EarningsChartRoot>
    );
  }

  const onMouseEnter = () => {
    chartGA('show_earnings_chart_details', {
      assetName,
      cursorAction: 'hover',
      action: 'show',
    });
  };

  const onChartClick = () => {
    chartGA('show_earnings_chart_details', {
      assetName,
      cursorAction: 'click',
      action: 'show',
    });
  };

  return (
    <S.EarningsChartRoot data-testid="earnings-chart">
      <S.Title>
        <ChartTitle
          count={historyMetaCount}
          assetDetails={assetDetails}
          expectation={expectation}
        />
      </S.Title>
      <S.Content>
        <ChartLegend />
        <S.ChartWrapper
          ref={chartWrapperRef}
          onMouseEnter={onMouseEnter}
          onClick={onChartClick}
          data-testid="chart-container"
        >
          <HighLowChart
            data={mappedData}
            width={width}
            height={216}
            padding={width > 500 ? paddingDesktop : paddingMobile}
          />
        </S.ChartWrapper>
        <ChartFooter />
      </S.Content>
    </S.EarningsChartRoot>
  );
};
